import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Play, Copy, Check, Gamepad2, MessageSquare, ExternalLink, ThumbsUp, ChevronDown, BarChart2, Video, Server, Users, Shield, Wifi, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export const fetchServerStatus = async (address) => {
  try {
    const res = await fetch(`/api/servers/status?address=${encodeURIComponent(address)}`);
    if (!res.ok) throw new Error('Failed to fetch server status');
    return await res.json();
  } catch (err) {
    return {
      online: false,
      players: 0,
      max_players: 32,
      map: 'de_dust2',
      ping: 0,
    };
  }
};

const MAP_IMAGES = {
  de_dust2: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  de_inferno: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  de_nuke: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_cbble: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  cs_747: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  de_mirage: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"),
  cs_office: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_nuke.jpg"),
  cs_italy: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_inferno.jpg"),
  default: ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg")
};

const getMapImage = (mapName) => {
  if (!mapName) return MAP_IMAGES.default;
  const m = mapName.toLowerCase().trim();
  if (m.includes("dust2") || m.includes("dust")) return MAP_IMAGES.de_dust2;
  if (m.includes("inferno")) return MAP_IMAGES.de_inferno;
  if (m.includes("cbble") || m.includes("cobble")) return MAP_IMAGES.de_cbble;
  if (m.includes("747")) return MAP_IMAGES.cs_747;
  if (m.includes("nuke")) return MAP_IMAGES.de_nuke;
  if (m.includes("mirage")) return MAP_IMAGES.de_mirage;
  if (m.includes("office")) return MAP_IMAGES.cs_office;
  if (m.includes("italy")) return MAP_IMAGES.cs_italy;
  return `https://image.gametracker.com/images/maps/160x120/cs/${m}.jpg`;
};

export const ServerStatusRow = ({ server, index, onSelect }) => {
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(12 + (index ? index * 7 : 4));

  const s = typeof server === 'string' ? { address: server } : (server || { address: "209.38.247.243:27016" });
  const address = s.address || "209.38.247.243:27016";
  const gameType = (s.game_type || (address.includes("CrGC") ? "discord" : "cs16")).toLowerCase();
  const isDiscord = gameType === 'discord';

  const { data, isLoading } = useQuery({
    queryKey: [isDiscord ? 'discordStatus' : 'serverStatus', address],
    queryFn: async () => {
      if (isDiscord) {
        const res = await fetch(`/api/discord/status?code=${address}`);
        return res.json();
      }
      return fetchServerStatus(address);
    },
    staleTime: 0,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const isOnline = data && data.online === true;
  const liveName = data?.name?.trim();
  const dbName = s?.name?.trim();
  const serverName = (liveName && liveName !== 'Counter-Strike 1.6 Server' && liveName !== 'Counter-Strike' && liveName !== 'Half-Life')
    ? liveName
    : (dbName || liveName || address);

  const currentMap = data?.map || 'de_dust2';
  const players = isDiscord ? (data?.presence_count ?? 25) : (data?.players ?? 0);
  const maxPlayers = isDiscord ? (data?.total_members ?? 531) : (data?.max_players ?? 32);
  const fillPct = maxPlayers > 0 ? Math.min(100, Math.max(0, Math.round((players / maxPlayers) * 100))) : 0;
  const dns = s.dns || (isDiscord ? `discord.gg/${address}` : address);

  const handleConnect = (e) => {
    if (e) e.stopPropagation();
    if (isDiscord) {
      window.open(data?.instant_invite || `https://discord.gg/${address}`, '_blank');
    } else {
      window.location.href = `steam://connect/${address}`;
    }
  };

  const handleCopy = (e) => {
    if (e) e.stopPropagation();
    const textToCopy = isDiscord ? (data?.instant_invite || `https://discord.gg/${address}`) : address;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success(`Copiat: ${textToCopy}`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLike = (e) => {
    if (e) e.stopPropagation();
    if (!liked) {
      setLiked(true);
      setLikeCount(prev => prev + 1);
      toast.success("Ai apreciat acest server!");
    } else {
      setLiked(false);
      setLikeCount(prev => prev - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-[#090c12] border border-[#141824] p-3 flex items-center justify-between animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-white/10 rounded-full" />
          <div className="w-9 h-9 bg-white/10" />
          <div className="h-4 bg-white/10 w-48" />
        </div>
        <div className="h-6 bg-white/10 w-24" />
      </div>
    );
  }

  return (
    <div 
      onClick={() => onSelect && onSelect(s)}
      className="group bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] hover:border-[var(--tf-primary)]/50 p-2.5 md:p-3 flex items-center gap-3 transition-all duration-200 shadow-md font-mono text-xs cursor-pointer relative"
    >
      {/* 1. Status pulse dot */}
      <div className="relative shrink-0 flex items-center justify-center">
        <span 
          className={`w-2.5 h-2.5 rounded-full ${isOnline ? 'bg-emerald-400 shadow-[0_0_8px_#34d399] animate-pulse' : 'bg-red-500 shadow-[0_0_6px_#ef4444]'}`} 
          title={isOnline ? 'Server Online' : 'Server Offline'} 
        />
      </div>

      {/* 2. Game Icon / Badge */}
      <div className="shrink-0 flex items-center justify-center">
        {(gameType === 'mix' || s.name?.toLowerCase().includes('mix') || address.includes('27017')) ? (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-purple-500/20 text-purple-400 border border-purple-500/30 font-chakra">
            MIX
          </span>
        ) : gameType === 'cs2' ? (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-orange-500/20 text-orange-400 border border-orange-500/30 font-chakra">
            CS2
          </span>
        ) : isDiscord ? (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-[#5865F2]/20 text-[#5865F2] border border-[#5865F2]/40 font-chakra">
            DC
          </span>
        ) : (
          <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-chakra">
            CS 1.6
          </span>
        )}
      </div>

      {/* 3. Map / Discord Avatar Thumbnail */}
      {isDiscord ? (
        <div className="w-9 h-9 overflow-hidden border border-[#5865F2]/40 bg-black/60 shrink-0">
          <img
            src={data?.icon_url || "https://cdn.discordapp.com/icons/1193299540019794161/eb3c6c5650b82001b8488949bfc30c68.png?size=256"}
            alt="Discord"
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="relative w-9 h-9 overflow-hidden border border-[#141824] bg-black/60 shrink-0">
          <img
            src={getMapImage(currentMap)}
            alt={currentMap}
            onError={(e) => { e.target.onerror = null; e.target.src = ((process.env.PUBLIC_URL || "/forum") + "/maps/de_dust2.jpg"); }}
            className="w-full h-full object-cover"
          />
          <span className="absolute bottom-0 inset-x-0 bg-black/90 text-[7px] font-mono text-center text-amber-400 truncate leading-3 py-px">
            {currentMap}
          </span>
        </div>
      )}

      {/* 4. Server Name & DNS Subtitle */}
      <div className="min-w-0 flex-1 flex flex-col justify-center">
        <div className="font-chakra font-black text-white group-hover:text-[var(--tf-primary)] transition-colors uppercase tracking-wide text-xs truncate">
          {serverName}
        </div>
        <div className="text-[10px] font-mono text-zinc-400 truncate flex items-center gap-1.5 mt-0.5">
          <span className="text-zinc-500">IP:</span>
          <span className="text-zinc-300 group-hover:text-white transition-colors">{dns}</span>
        </div>
      </div>

      {/* 5. Connect Button */}
      <button
        onClick={handleConnect}
        className="tf-btn-primary tf-clip-btn px-3 py-1.5 text-xs font-black uppercase tracking-wider flex items-center gap-1 shrink-0 shadow-md hover:scale-105 transition-transform"
      >
        <span>CONNECT</span>
        <Play className="w-2.5 h-2.5 fill-current" />
      </button>

      {/* 6. Circular Progress Gauge + Player Count */}
      <div className="hidden sm:flex items-center gap-2 shrink-0 bg-black/40 px-2.5 py-1 border border-white/5">
        <div className="relative w-7 h-7 flex items-center justify-center shrink-0">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
            <path className="text-zinc-800" strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path 
              className={isDiscord ? "text-[#5865F2]" : players > 0 ? "text-emerald-400" : "text-[var(--tf-primary)]"} 
              strokeDasharray={`${fillPct}, 100`} 
              strokeWidth="4" 
              strokeLinecap="round" 
              stroke="currentColor" 
              fill="none" 
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" 
            />
          </svg>
          <span className="absolute text-[7px] font-black text-white font-mono">{fillPct}%</span>
        </div>
        
        <div className="font-mono text-xs">
          {isDiscord ? (
            <span className="text-zinc-300 font-bold">{players} online</span>
          ) : (
            <div className="flex items-center gap-1">
              <span className={`font-black ${players > 0 ? "text-emerald-400 font-bold" : "text-zinc-400"}`}>
                {players}
              </span>
              <span className="text-zinc-500">/</span>
              <span className="text-zinc-400 font-bold">{maxPlayers}</span>
            </div>
          )}
        </div>
      </div>

      {/* 7. Action Buttons */}
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          onClick={handleLike}
          className={`px-2 py-1.5 border text-xs flex items-center gap-1 transition-all ${
            liked 
              ? 'bg-[var(--tf-primary)]/20 border-[var(--tf-primary)] text-[var(--tf-primary)] font-bold' 
              : 'bg-white/5 border-white/10 text-zinc-400 hover:text-white hover:bg-white/10'
          }`}
          title="Apreciază serverul"
        >
          <ThumbsUp className="w-3 h-3" />
          <span className="text-[10px] font-mono hidden md:inline">{likeCount}</span>
        </button>

        <button
          onClick={handleCopy}
          className="p-1.5 bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] text-zinc-400 hover:text-white transition-colors cursor-pointer"
          title="Copiază IP"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>

        {s.forum_board_id && (
          <Link
            to={`/forums/${s.forum_board_id}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] text-zinc-400 hover:text-white transition-colors"
            title="Secțiune Forum"
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </Link>
        )}

        <button
          onClick={(e) => { e.stopPropagation(); onSelect && onSelect(s); }}
          className="px-2.5 py-1.5 bg-[#06080d] hover:bg-[#0c1018] border border-[#141824] text-zinc-300 hover:text-white text-[11px] font-bold transition-colors flex items-center gap-1 font-chakra uppercase cursor-pointer"
        >
          <span>Detalii</span>
          <ChevronDown className="w-3 h-3 text-[var(--tf-primary)]" />
        </button>
      </div>
    </div>
  );
};

export const ServerSummaryFooter = ({ servers = [] }) => {
  const [stats, setStats] = useState({
    site: 48,
    servers: 64,
    mods: {
      PUBLIC: 28,
      RESPAWN: 18,
      MIX: 10,
      FUN: 8
    }
  });

  useEffect(() => {
    const updateStats = async () => {
      try {
        const res = await api.get("/widgets/live-metrics");
        if (res.data) {
          setStats({
            site: res.data.online_users || 48,
            servers: res.data.total_players || 64,
            mods: res.data.server_mods || {
              PUBLIC: 28,
              RESPAWN: 18,
              MIX: 10,
              FUN: 8
            }
          });
        }
      } catch {
        // graceful fallback
      }
    };
    updateStats();
    const interval = setInterval(updateStats, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#06080d] border border-[#141824] px-4 py-3 flex flex-wrap items-center justify-between gap-4 text-xs font-mono rounded-xl">
      {/* Total Servere & Site Online */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span 
            className="px-2 py-0.5 text-black font-black font-chakra text-[11px] rounded"
            style={{ backgroundColor: "var(--tf-primary)" }}
          >
            {servers.length || 4}
          </span>
          <span className="text-zinc-300 font-bold uppercase tracking-wider text-[11px] font-chakra" id="servers_total">Total Servere</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_#34d399]" />
          <span className="text-zinc-300 font-bold uppercase tracking-wider text-[11px] font-chakra">
            Site Online: <strong className="text-emerald-400" id="site_online">{stats.site}</strong>
          </span>
        </div>
      </div>

      {/* Mod breakdown stats badges (Script 3) */}
      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
        <span className="text-zinc-500 uppercase font-bold">Jucători pe Moduri:</span>
        <span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded" id="mod_PUBLIC">
          PUBLIC: <strong>{stats.mods.PUBLIC}</strong>
        </span>
        <span className="px-2 py-0.5 bg-orange-500/10 border border-orange-500/20 text-orange-400 rounded" id="mod_RESPAWN">
          RESPAWN: <strong>{stats.mods.RESPAWN}</strong>
        </span>
        <span className="px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded" id="mod_MIX">
          MIX: <strong>{stats.mods.MIX}</strong>
        </span>
        <span className="px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded" id="mod_FUN">
          FUN: <strong>{stats.mods.FUN}</strong>
        </span>
      </div>
    </div>
  );
};

export const ServerStatusWidget = ({ address, label, server, onSelect }) => {
  return <ServerStatusRow server={server || { address, name: label }} onSelect={onSelect} />;
};
